import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Heart, Users, Award, MessageSquare, HandHeart, Clock, CheckCircle, ArrowRight, Plus, } from 'lucide-react'
import type { Testimonial, Partner, Donation, UserProfile } from '@/types'

const TEAL = '#5BBDB5'
const TEAL_BG = '#DCF0EE'
const GOLD = '#C8922A'
const NAVY = '#1E3A4A'

export default function DashboardHome() {
  const [stats, setStats] = useState({
    donations: 0,
    donationsPending: 0,
    testimonialsPending: 0,
    partners: 0,
    users: 0,
  })
  const [pendingTestimonials, setPendingTestimonials] = useState<Testimonial[]>([])
  const [recentDonations, setRecentDonations] = useState<Donation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [donationsRes, testimonialsRes, partnersRes, usersRes] = await Promise.all([
          lumi.entities.donations.list({ sort: { createdAt: -1 }, limit: 50 }),
          lumi.entities.testimonials.list({ filter: { status: 'pending' }, sort: { createdAt: -1 }, limit: 20 }),
          lumi.entities.partners.list({ filter: { isActive: true }, limit: 50 }),
          lumi.entities.user_profiles.list({ limit: 50 }),
        ])

        const allDonations = donationsRes.list as Donation[]
        const pendingT = testimonialsRes.list as Testimonial[]
        const allPartners = partnersRes.list as Partner[]
        const allUsers = usersRes.list as UserProfile[]

        setStats({
          donations: allDonations.length,
          donationsPending: allDonations.filter(d => d.status === 'pending').length,
          testimonialsPending: pendingT.length,
          partners: allPartners.length,
          users: allUsers.length,
        })
        setPendingTestimonials(pendingT.slice(0, 5))
        setRecentDonations(allDonations.slice(0, 5))
      } catch (e) {
        console.error('Erro ao carregar estatísticas:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  const cards = [
    {
      label: 'Total de Doações',
      value: stats.donations,
      icon: Heart,
      color: GOLD,
      bg: '#fef9ee',
      link: null,
    },
    {
      label: 'Doações Pendentes',
      value: stats.donationsPending,
      icon: Clock,
      color: '#e67e22',
      bg: '#fff8f0',
      link: null,
    },
    {
      label: 'Depoimentos Pendentes',
      value: stats.testimonialsPending,
      icon: MessageSquare,
      color: TEAL,
      bg: TEAL_BG,
      link: '/admin/depoimentos',
    },
    {
      label: 'Parceiros Ativos',
      value: stats.partners,
      icon: Award,
      color: NAVY,
      bg: '#eef2f5',
      link: '/admin/parceiros',
    },
  ]

  const donationStatusColor: Record<string, string> = {
    pending: GOLD,
    confirmed: TEAL,
    rejected: '#ef4444',
  }
  const donationStatusLabel: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmada',
    rejected: 'Rejeitada',
  }

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">

      {/* Cabeçalho */}
      <div className="mb-6 sm:mb-8 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Visão Geral</h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            Bem-vindo ao painel do <strong>PALACIO DA SABEDORIA</strong>
          </p>
        </div>
        <Link
          to="/admin/landing"
          className="flex items-center gap-2 px-3 sm:px-4 py-2 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors flex-shrink-0"
          style={{ background: TEAL }}
          onMouseEnter={e => (e.currentTarget.style.background = '#4aaba3')}
          onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
        >
          <Plus size={15} />
          <span className="hidden sm:inline">Configurar Landing</span>
          <span className="sm:hidden">Config.</span>
        </Link>
      </div>

      {/* Cards de estatísticas */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {cards.map(card => {
          const Icon = card.icon
          const inner = (
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5 hover:shadow-sm transition-shadow">
              <div
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mb-3"
                style={{ background: card.bg }}
              >
                <Icon size={18} style={{ color: card.color }} />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {loading ? '—' : card.value}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">{card.label}</p>
              {card.link && (
                <span className="text-xs font-semibold mt-2 inline-flex items-center gap-1" style={{ color: TEAL }}>
                  Ver todos <ArrowRight size={11} />
                </span>
              )}
            </div>
          )
          return card.link ? (
            <Link key={card.label} to={card.link}>{inner}</Link>
          ) : (
            <div key={card.label}>{inner}</div>
          )
        })}
      </div>

      {/* Dois painéis lado a lado */}
      <div className="grid lg:grid-cols-2 gap-6">

        {/* Doações recentes */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <HandHeart size={16} style={{ color: GOLD }} />
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Doações Recentes</h2>
            </div>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {loading ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">A carregar...</div>
            ) : recentDonations.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <Heart size={28} className="mx-auto mb-2 text-gray-200" />
                <p className="text-sm text-gray-400">Nenhuma doação registada ainda.</p>
              </div>
            ) : recentDonations.map(donation => (
              <div key={donation._id} className="flex items-center gap-3 px-4 sm:px-5 py-3">
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-xs"
                  style={{ background: GOLD }}
                >
                  {donation.donorName?.charAt(0)?.toUpperCase() || 'D'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">
                    {donation.donorName}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(donation.createdAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm font-bold text-gray-900 dark:text-white">
                    {donation.amount
                      ? `R$ ${Number(donation.amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                      : '—'
                    }
                  </span>
                  <span
                    className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      background: (donationStatusColor[donation.status] || '#9ca3af') + '18',
                      color: donationStatusColor[donation.status] || '#9ca3af',
                    }}
                  >
                    {donationStatusLabel[donation.status] || donation.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Depoimentos pendentes */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <MessageSquare size={16} style={{ color: TEAL }} />
              <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Depoimentos Aguardando</h2>
            </div>
            <Link
              to="/admin/depoimentos"
              className="text-xs font-semibold flex items-center gap-1 hover:underline"
              style={{ color: TEAL }}
            >
              Ver todos <ArrowRight size={12} />
            </Link>
          </div>
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {loading ? (
              <div className="px-5 py-8 text-center text-sm text-gray-400">A carregar...</div>
            ) : pendingTestimonials.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <CheckCircle size={28} className="mx-auto mb-2 text-gray-200" />
                <p className="text-sm text-gray-400">Nenhum depoimento pendente. 🎉</p>
              </div>
            ) : pendingTestimonials.map(t => (
              <div key={t._id} className="flex items-start gap-3 px-4 sm:px-5 py-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-xs mt-0.5"
                  style={{ background: TEAL }}
                >
                  {t.authorName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-gray-900 dark:text-white">{t.authorName}</p>
                  {t.authorRole && <p className="text-xs text-gray-400">{t.authorRole}</p>}
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 leading-relaxed italic">
                    "{t.text}"
                  </p>
                </div>
                <Link
                  to="/admin/depoimentos"
                  className="text-xs px-2.5 py-1.5 rounded-lg font-semibold text-white flex-shrink-0 transition-all"
                  style={{ background: TEAL }}
                  onMouseEnter={e => (e.currentTarget.style.background = '#4aaba3')}
                  onMouseLeave={e => (e.currentTarget.style.background = TEAL)}
                >
                  Analisar
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Acções rápidas */}
      <div className="mt-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
        <h2 className="font-semibold text-gray-900 dark:text-white text-sm mb-4">Acções Rápidas</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Configurar Landing', icon: Plus, to: '/admin/landing', color: TEAL },
            { label: 'Gerir Depoimentos', icon: MessageSquare, to: '/admin/depoimentos', color: TEAL },
            { label: 'Gerir Parceiros', icon: Award, to: '/admin/parceiros', color: NAVY },
            { label: 'Gerir Utilizadores', icon: Users, to: '/admin/usuarios', color: GOLD },
          ].map(({ label, icon: Icon, to, color }) => (
            <Link
              key={label}
              to={to}
              className="flex flex-col items-center gap-2 p-4 rounded-xl border border-gray-100 dark:border-gray-800 hover:shadow-sm transition-all text-center"
              style={{ background: color + '0d' }}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: color + '22' }}>
                <Icon size={18} style={{ color }} />
              </div>
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 leading-tight">{label}</span>
            </Link>
          ))}
        </div>
      </div>

    </div>
  )
}
